import React, { useContext, useEffect, useState } from 'react';
import { Card, Snackbar, Alert, Box, Button } from '@mui/material'; // Added Button
import { styled } from '@mui/material/styles'; // Added styled
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { OrgContext } from 'context/orgContext';
import { UserContext } from 'context/userContext';
import { useNotifications } from 'context/NotificationContext';
import { userOrgs } from 'services/org';
import { getAllVersionsForPatch } from 'services/product';

import DeployEnvironment from './components/DeployEnvironment';
import DeployProduct from './components/DeployProduct';
import DeployReview from './components/DeployReview';
import DeployCommit from './components/DeployCommit';

const getProductGroupId = (product) => product.external_id || product.sf_id || product.name;

// --- Styled Buttons for Navigation ---
const NextButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.secondary.dark,
  },
}));

const BackButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.secondary.dark,
  },
}));

function Deploy() {
  const [activeStage, setActiveStage] = useState('environment');
  const [isStagesRendered, setIsStagesRendered] = useState(false);
  const { userId } = useContext(UserContext);
  const { orgs, setOrgs } = useContext(OrgContext);
  const { addNotification } = useNotifications();

  const [selectedOrg, setSelectedOrg] = useState(null);
  const [deployableProducts, setDeployableProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [versionChoices, setVersionChoices] = useState({});
  const [commitProducts, setCommitProducts] = useState([]);
  const [isReviewCompleted, setIsReviewCompleted] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  useEffect(() => {
    const fetchOrgs = async () => {
      if (!userId) return;
      try {
        const { orgs: f } = await userOrgs();
        setOrgs(f || []);
      } catch (e) {
        console.error(e);
      } finally {
        setIsStagesRendered(true);
      }
    };
    if (!orgs || orgs.length === 0) fetchOrgs();
    else setIsStagesRendered(true);
  }, [userId, orgs, setOrgs]);

  useEffect(() => {
    const fetchProducts = async () => {
      if (!selectedOrg?.org_id) {
        setDeployableProducts([]);
        return;
      }
      
      // Only fetch when on environment or products stage to ensure fresh data
      if (activeStage !== 'environment' && activeStage !== 'products') {
        return;
      }
      
      try {
        console.log('[Deploy] Fetching all product versions for deployment...');
        const response = await getAllVersionsForPatch(selectedOrg.org_id, []);
        
        if (response?.error || !response?.products) {
          console.error('Error fetching deployable products:', response?.message || 'Unknown error');
          addNotification({
            title: ['Error', 'Product Fetch Failed'],
            severity: 'error',
            details: response?.message || 'Could not fetch products for deployment.',
          });
          setDeployableProducts([]);
        } else {
          console.log(`[Deploy] Found ${response.products.length} product versions for deployment`);
          setDeployableProducts(response.products);
        }
      } catch (error) {
        console.error('Error fetching deployable products:', error);
        addNotification({
          title: ['Error', 'Product Fetch Failed'],
          severity: 'error',
          details: error.message,
        });
        setDeployableProducts([]);
      }
    };
    fetchProducts();
  }, [selectedOrg, activeStage, addNotification]);

  const handleClearSelections = () => {
    setSelectedProducts([]);
    setVersionChoices({});
  };

  const handleEnvSelect = (orgObject) => {
    setSelectedOrg(orgObject);
    handleClearSelections();
  };

  const handleVersionChange = (productGroup, newVersion) => {
    const { groupId, name, org_id } = productGroup;
    setVersionChoices((prev) => ({ ...prev, [groupId]: newVersion }));

    // Check if any product with the same name and org_id is currently selected
    const isCurrentlySelected = selectedProducts.some(
      (p) => p.name === name && (p.org_id || '') === (org_id || '')
    );
    
    if (isCurrentlySelected) {
      // Find the product with the new version, matching by name, org_id, and version
      const fullProductForNewVersion = deployableProducts.find(
        (p) => p.name === name && 
               (p.org_id || '') === (org_id || '') && 
               p.product_version === newVersion
      );
      if (fullProductForNewVersion) {
        setSelectedProducts((prev) =>
          prev.map((p) => 
            (p.name === name && (p.org_id || '') === (org_id || '')) 
              ? fullProductForNewVersion 
              : p
          )
        );
        // Version updated silently
      }
    } else {
      // Version staged silently
    }
  };

  const handleToggleSelection = (productGroup) => {
    const { groupId, name, org_id } = productGroup;
    // Check if product with same name and org_id is selected
    const isSelected = selectedProducts.some(
      (p) => p.name === name && (p.org_id || '') === (org_id || '')
    );

    if (isSelected) {
      // Remove product with same name and org_id
      setSelectedProducts((prev) => 
        prev.filter((p) => !(p.name === name && (p.org_id || '') === (org_id || '')))
      );
      // Product removed silently
    } else {
      const chosenVersion = versionChoices[groupId];
      if (!chosenVersion) {
        addNotification({
          title: ['Action Required', 'Select Version'],
          details: `Select a version for '${name}' first.`,
          severity: 'error',
        });
        return;
      }
      // Find product with matching name, org_id, and chosen version
      const productToAdd = deployableProducts.find(
        (p) => p.name === name && 
               (p.org_id || '') === (org_id || '') && 
               p.product_version === chosenVersion
      );

      if (productToAdd) {
        setSelectedProducts((prev) => [...prev, productToAdd]);
        // Product added silently
      }
    }
  };

  const handleReviewCompletion = (finalProducts) => {
    setCommitProducts(finalProducts);
    setIsReviewCompleted(true);
  };

  const handleStageChange = (stage, options = {}) => {
    if (stage !== 'environment' && !selectedOrg) {
      setSnackbarMessage('Please select an environment before proceeding.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    if (options.isSuccessReset) {
      // This is a reset after a successful action.
      // Clear product-related state but keep the selected org.
      setSelectedProducts([]);
      setVersionChoices({});
      setCommitProducts([]);
      setIsReviewCompleted(false);
    }

    setActiveStage(stage);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  // --- Renders navigation buttons based on the current stage ---
  const renderNavigation = () => {
    const commonButtonStyles = {
      mt: 3,
      display: 'flex',
      justifyContent: 'space-between',
      gap: 2,
    };

    switch (activeStage) {
      case 'environment':
        return (
          <ArgonBox sx={{ ...commonButtonStyles, justifyContent: 'flex-end' }}>
            <NextButton variant="contained" onClick={() => handleStageChange('products')} disabled={!selectedOrg}>
              Select Products &rarr;
            </NextButton>
          </ArgonBox>
        );
      case 'products':
        return (
          <ArgonBox sx={commonButtonStyles}>
            <BackButton variant="contained" onClick={() => handleStageChange('environment')}>
              &larr; Select Environment
            </BackButton>
            <NextButton
              variant="contained"
              onClick={() => handleStageChange('review')}
              disabled={selectedProducts.length === 0}
            >
              Review Changes &rarr;
            </NextButton>
          </ArgonBox>
        );
      case 'review':
        return (
          <ArgonBox sx={commonButtonStyles}>
            <BackButton variant="contained" onClick={() => handleStageChange('products')}>
              &larr; Select Products
            </BackButton>
            <NextButton variant="contained" onClick={() => handleStageChange('commit')} disabled={!isReviewCompleted}>
              Proceed to Deploy &rarr;
            </NextButton>
          </ArgonBox>
        );
      default:
        return null; // No navigation for commit stage here, it's in the component
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox display="flex" justifyContent="space-between" alignItems="center" p={3} flexWrap="wrap">
              <ArgonTypography
                variant="button"
                color={activeStage === 'environment' ? 'info' : 'text'}
                fontWeight="regular"
                onClick={() => handleStageChange('environment')}
                sx={{ cursor: 'pointer' }}
              >
                Select Environment
              </ArgonTypography>
              <ArgonTypography> → </ArgonTypography>
              <ArgonTypography
                variant="button"
                color={activeStage === 'products' ? 'info' : 'text'}
                fontWeight="regular"
                onClick={() => handleStageChange('products')}
                sx={{ cursor: selectedOrg ? 'pointer' : 'default', opacity: selectedOrg ? 1 : 0.5 }}
              >
                Select Products
              </ArgonTypography>
              <ArgonTypography> → </ArgonTypography>
              <ArgonTypography
                variant="button"
                color={activeStage === 'review' ? 'info' : 'text'}
                fontWeight="regular"
                onClick={() => handleStageChange('review')}
                sx={{
                  cursor: selectedProducts.length > 0 ? 'pointer' : 'default',
                  opacity: selectedProducts.length > 0 ? 1 : 0.5,
                }}
              >
                Review Changes
              </ArgonTypography>
              <ArgonTypography> → </ArgonTypography>
              <ArgonTypography
                variant="button"
                color={activeStage === 'commit' ? 'info' : 'text'}
                fontWeight="regular"
                onClick={() => handleStageChange('commit')}
                sx={{ cursor: isReviewCompleted ? 'pointer' : 'default', opacity: isReviewCompleted ? 1 : 0.5 }}
              >
                Deploy Changes
              </ArgonTypography>
            </ArgonBox>
          </Card>

          {isStagesRendered && (
            <Box>
              <Box style={{ display: activeStage === 'environment' ? 'block' : 'none' }}>
                <DeployEnvironment envs={orgs || []} selected={selectedOrg} selectedEnv={handleEnvSelect} />
              </Box>
              <Box style={{ display: activeStage === 'products' ? 'block' : 'none' }}>
                <DeployProduct
                  orgs={orgs || []}
                  variants={deployableProducts}
                  selectedProducts={selectedProducts}
                  versionChoices={versionChoices}
                  onVersionChange={handleVersionChange}
                  onToggleSelection={handleToggleSelection}
                  onClearSelections={handleClearSelections}
                />
              </Box>
              <Box style={{ display: activeStage === 'review' ? 'block' : 'none' }}>
                {activeStage === 'review' && (
                  <DeployReview
                    org={selectedOrg}
                    orgId={selectedOrg?.org_id}
                    isRevenue={selectedOrg?.type === 'RC'}
                    selectedProducts={selectedProducts}
                    onReviewComplete={handleReviewCompletion}
                  />
                )}
              </Box>
              <Box style={{ display: activeStage === 'commit' ? 'block' : 'none' }}>
                {activeStage === 'commit' && (
                  <DeployCommit
                    userId={userId}
                    org={selectedOrg}
                    commitProducts={commitProducts}
                    handleStageChange={handleStageChange}
                  />
                )}
              </Box>
            </Box>
          )}
          {isStagesRendered && renderNavigation()}
        </ArgonBox>
      </ArgonBox>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} variant="filled" severity={snackbarSeverity} sx={{ alignItems: 'center' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default Deploy;
